import { assertPresetAllowed } from '../access.js';
import { generateId } from '@studio/models';
import type { Group } from '@studio/models/control/preset';
import type { StudioDeviceControlPresetGroup } from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { AddGroup } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import type { OmitSyncProps } from '#~/api/index.js';
import { StudioError } from '#~/error/index.js';

/**
 * Add a position preset group for the specified device.
 *
 * @param deviceId - The ID of the device for which a group is to be added.
 * @param initial - Initial group values.
 * @returns Group ID.
 */
export const addGroup: AddGroup = async (deviceId, initial, token): Promise<Group['id']> => {
  try {
    assertPresetAllowed(deviceId);
    const proxy = locateProxy(deviceId);

    if (!proxy) {
      throw new Error('CG-213');
    }

    // Generate an ID for this group.
    const groupId = generateId<Group['id']>();

    // Define mutation.
    const mutation: OmitSyncProps<StudioDeviceControlPresetGroup> = {
      name: initial.name,
      rank: initial.rank
    };

    // Send mutation
    await proxy.mutate(
      {
        control: {
          studio: {
            device: {
              [deviceId]: {
                control: {
                  presets: {
                    groups: {
                      [groupId]: mutation
                    }
                  }
                }
              }
            }
          }
        }
      },
      {}
    );

    await proxy.save(token);

    return groupId;
  } catch (cause) {
    throw new StudioError('Unable to add preset group.', { cause });
  }
};
